/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { GraphicalTakeOffPoint } from '@gh/builder-shared-data';
import {
  isDirty,
  PlainImmutablePageState,
  removeEntityField,
  setDirty,
  setDirtyField,
  setEntityField,
  setEntityFields,
  setNestedEntityField,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { isNotNil } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { distinctUntilChanged$, map$ } from '@gh/rx/operators';
import {
  constant,
  difference,
  find,
  first,
  flatMap,
  flow,
  forEach,
  fromPairs,
  groupBy,
  identity,
  isNil,
  keys,
  last,
  map,
  omit,
  pick,
  reduce,
  sum,
  uniq,
  values,
} from 'lodash';
import { GraphicalTakeOffMainConfig } from './components/main';
import {
  addPoint,
  addPointIntoAreaMeasurement,
  addPointIntoLengthMeasurement,
  appendGtoTopViewMeasurements,
  calculateArea,
  commitPointForTop,
  consistencyTopViewTransform,
  convertValueUom,
  createAreaMeasurement,
  createCountMeasurement,
  createDatabox,
  createLengthMeasurement,
  findGtoChangedViewIds,
  getDataBoxOriginalValue,
  getGraphicalTakeOffBoxId,
  getGraphicalTakeOffHousePlanViewId,
  getGraphicalTakeOffMeasurementId,
  getGraphicalTakeOffTopId,
  getGraphicalTakeOffTopType,
  getGraphicalTakeOffTopViewAllMeasurements,
  getGraphicalTakeOffTopViewId,
  getGraphicalTakeOffTopViewLastMeasurement,
  getGraphicalTakeOffViewImage,
  getGraphicalTakeOffViewImagePipeline,
  getGtoAffectedViewsHaingAnotherNextRecord,
  getGtoAffectedViewsHavingAnotherCurrentRecord,
  getGtoNextViewHistoryRecords,
  getGtoPreviousViewHistoryRecords,
  getGtoViewHistoryRecord,
  GRAPHICAL_TAKE_OFF_DETAILS_INIT,
  GraphicalTakeOffBox,
  graphicalTakeOffBoxArr,
  GraphicalTakeOffChangeOption,
  GraphicalTakeOffDataBoxVisibility,
  GraphicalTakeOffDetails,
  GraphicalTakeOffHousePlanView,
  graphicalTakeOffHousePlanViewArr,
  graphicalTakeOffImageAppendTransform,
  graphicalTakeOffImagePipeline,
  graphicalTakeOffImageSetTransformation,
  GraphicalTakeOffImageTransform,
  GraphicalTakeOffMeasurement,
  graphicalTakeOffMeasurementArr,
  GraphicalTakeOffMeasurementStatus,
  GraphicalTakeOffScaleDirection,
  GraphicalTakeOffTop,
  graphicalTakeOffTopArr,
  GraphicalTakeOffTopView,
  graphicalTakeOffTopViewArr,
  graphicalTakeOffTopViewCurrentSelectionValue,
  graphicalTakeOffTopViewValue,
  GraphicalTakeOffViewInfo,
  GraphicalTakeOffViewTransform, GTO_SCALING_TOOL_INIT, GTO_TOOLS_INIT,
  GTO_VIEW_HISTORY_EMPTY,
  gtoRestoreDetails,
  GtoToolName,
  isGraphicalTakeOffAreaType,
  isGraphicalTakeOffCountType,
  isGraphicalTakeOffLengthType,
  isGraphicalTakeOffTopViewCanSnapToStart,
  isGraphicalTakeOffTopViewNonMigrated,
  isGraphicalTakeOffTopViewValid,
  middlePoint,
  normalizePointsForClone,
  pushGtoViewHistoryRecords,
  queryGtoScaleLinePxLength,
  removeGtoTopViewMeasurements,
  removeMeasurementPoint,
  rollbackPointForTop,
  scaleArea,
  scaleLength,
  scalePoint,
  setDataboxOriginalValue,
  setPointForAreaMeasurement,
  setPointForCountMeasurement,
  setPointForLengthMeasurement,
  shouldAppendMeasurement, truncateValueByUom,
  UserGTOPreferences,
} from './resources';

@Injectable()
export class GraphicalTakeOffFormState extends PlainImmutablePageState<GraphicalTakeOffDetails> {
  tops$ = this.select(({ tops }) => tops);
  currentViewId$ = this.select(({ currentView }) => currentView);
  views$ = this.select(({ views }) => views);
  currentView$ = this.select(({ views, currentView }) => graphicalTakeOffHousePlanViewArr.find(views, currentView)!);
  topViews$ = this.select(({ topViews }) => topViews);
  currentTopViews$ = combineLatest$(this.currentViewId$, this.topViews$).pipe(
    map$(([currentViewId, topViews]) => topViews[currentViewId]),
    distinctUntilChanged$());
  isOverlay$ = this.select(({ isOverlay }) => isOverlay);
  dataBoxVisibility$ = this.select(({ dataBoxVisibility }) => dataBoxVisibility);
  currentTopId$ = this.select(({ currentTop }) => currentTop);
  visibleTopViews$ = combineLatest$(this.currentTopViews$, this.isOverlay$, this.currentTopId$).pipe(
    map$(([topViews, isOverlay, currentTop]) =>
      isOverlay ?
        topViews
        : topViews?.filter((topView) => getGraphicalTakeOffTopViewId(topView) === currentTop)));
  viewTransform$ = this.select(({ viewTransform }) => viewTransform);
  zoom$ = this.select(({ zoom }) => zoom);
  highlightedTop$ = this.select(({ highlightedTop }) => highlightedTop);
  highlightedMeasurement$ = this.select(({ highlightedMeasurement }) => highlightedMeasurement);
  highlightedDataBox$ = this.select(({ highlightedDataBox }) => highlightedDataBox);
  selectedTop$ = this.select(({ selectedTop }) => selectedTop);
  selectedMeasurement$ = this.select(({ selectedMeasurement }) => selectedMeasurement);
  currentTopView$ = combineLatest$(this.currentTopId$, this.currentTopViews$).pipe(
    map$(([currentTopId, topViews]) =>
      currentTopId ? graphicalTakeOffTopViewArr.find(topViews, currentTopId) : void 0));
  hasSelectedTop$ = this.currentTopView$.pipe(map$(Boolean));
  drawingMeasurementId$ = this.select(({ drawingMeasurement }) => drawingMeasurement);
  topStatus$ = combineLatest$(this.currentTopView$, this.drawingMeasurementId$).pipe(
    map$(([topView, drawingMeasurementId]) => {
      if (isNil(topView)) {
        return GraphicalTakeOffMeasurementStatus.STARTING;
      } else if (topView.metadata.measures.length === 0) {
        return GraphicalTakeOffMeasurementStatus.STARTING;
      } else if (isGraphicalTakeOffCountType(getGraphicalTakeOffTopType(topView))) {
        return GraphicalTakeOffMeasurementStatus.IN_PROGRESS;
      } else if (drawingMeasurementId) {
        if (this.isDrawingMeasurementValid) {
          return GraphicalTakeOffMeasurementStatus.IN_PROGRESS;
        } else {
          return GraphicalTakeOffMeasurementStatus.INCOMPLETE;
        }
      } else {
        return GraphicalTakeOffMeasurementStatus.COMPLETED;
      }
    }),
    distinctUntilChanged$());
  currentViewImage$ = this.currentView$.pipe(map$((currentView) => currentView ? currentView.image : void 0));
  currentViewImagePipeline$ = this.currentView$.pipe(map$((currentView) =>
    currentView ? currentView.imagePipeline : void 0));
  isCurrentViewScaled$ = this.currentView$.pipe(map$((currentView) => currentView ? currentView.isScaled : void 0));
  activeToolName$ = this.select(({ activeToolName }) => activeToolName);
  isScaleToolActive$ = this.activeToolName$.pipe(map$((toolName) => toolName === GtoToolName.SetScale));
  scalingTool = {
    point1$: this.select(({ tools: { scaling: { point1 } } }) => point1),
    point2$: this.select(({ tools: { scaling: { point2 } } }) => point2),
    currentDirection$: this.select(({ tools: { scaling: { currentDirection } } }) => currentDirection),
    isWaitingForLength$: this.select(({ tools: { scaling: { isWaitingForLength } } }) => isWaitingForLength),
    pxLength$: this.select(queryGtoScaleLinePxLength),
  };

  private hasPendingTransaction = false;
  private hasOpenedTransaction = false;
  private viewIdsAffectedByPendingTransaction: ID[] = [];

  constructor(private translations: TranslationService) {
    super(GRAPHICAL_TAKE_OFF_DETAILS_INIT);
  }

  get selectedTopId(): Maybe<ID> {
    return this.value.selectedTop;
  }

  get selectedMeasurementId(): Maybe<ID> {
    return this.value.selectedMeasurement;
  }

  get highlightedTopId(): Maybe<ID> {
    return this.value.highlightedTop;
  }

  get highlightedMeasurementId(): Maybe<ID> {
    return this.value.highlightedMeasurement;
  }

  get highlightedDataBoxId(): Maybe<ID> {
    return this.value.highlightedDataBox;
  }

  get views(): GraphicalTakeOffHousePlanView[] {
    return this.value.views;
  }

  get tops(): GraphicalTakeOffTop[] {
    return this.value.tops;
  }

  get currentTop(): Maybe<GraphicalTakeOffTop> {
    const { currentTop, tops } = this.value;

    return currentTop ? graphicalTakeOffTopArr.find(tops, currentTop) : void 0;
  }

  get currentTopViews(): GraphicalTakeOffTopView[] {
    return this.getTopViewsForView(this.value.currentView);
  }

  get currentTopId(): Maybe<ID> {
    return this.value.currentTop;
  }

  get currentViewId(): ID {
    return this.value.currentView;
  }

  get selectedViewId(): ID {
    return this.value.selectedView;
  }

  get currentTopView(): Maybe<GraphicalTakeOffTopView> {
    const { currentTop } = this.value;
    const { currentTopViews } = this;

    return currentTop ? graphicalTakeOffTopViewArr.find(currentTopViews, currentTop) : void 0;
  }

  get selectedTop(): Maybe<GraphicalTakeOffTopView> {
    const { selectedTop } = this.value;

    if (selectedTop) {
      return graphicalTakeOffTopViewArr.find(this.currentTopViews, selectedTop);
    }
  }

  get selectedMeasurement(): Maybe<GraphicalTakeOffMeasurement> {
    const { selectedTop } = this;
    const { selectedMeasurement } = this.value;

    if (selectedTop && selectedMeasurement) {
      return graphicalTakeOffMeasurementArr.find(selectedTop.metadata.measures, selectedMeasurement);
    }
  }

  get drawingTop(): Maybe<GraphicalTakeOffTopView> {
    const { drawingTop } = this.value;

    if (drawingTop) {
      return graphicalTakeOffTopViewArr.find(this.currentTopViews, drawingTop);
    }
  }

  get drawingMeasurement(): Maybe<GraphicalTakeOffMeasurement> {
    const { drawingTop } = this;
    const { drawingMeasurement } = this.value;

    if (drawingTop && drawingMeasurement) {
      return graphicalTakeOffMeasurementArr.find(drawingTop.metadata.measures, drawingMeasurement);
    }
  }

  get activeTop(): Maybe<GraphicalTakeOffTopView> {
    return this.drawingTop || this.selectedTop || this.currentTopView;
  }

  get activeMeasurement(): Maybe<GraphicalTakeOffMeasurement> {
    return this.drawingMeasurement || this.selectedMeasurement;
  }

  get currentView(): GraphicalTakeOffHousePlanView {
    const { views, currentView } = this.value;

    return currentView ? graphicalTakeOffHousePlanViewArr.find(views, currentView)! : <any>void 0;
  }

  get canDeleteSelectedMeasurement(): boolean {
    return isNotNil(this.selectedMeasurement) && (!this.isInTopDrawingMode || this.isDrawingMeasurementValid);
  }

  get isCurrentViewScaled(): boolean {
    return this.currentView.isScaled;
  }

  get horizontalScale(): number {
    return this.currentView.picture.scaleWidth || 0;
  }

  get verticalScale(): number {
    return this.currentView.picture.scaleHeight || 0;
  }

  get drawingMode(): boolean {
    const { currentView } = this;
    return currentView ? currentView.isScaled && this.value.activeToolName !== GtoToolName.SetScale : false;
  }

  get scaleMode(): boolean {
    const { currentView } = this;
    return currentView ? !currentView.isScaled : false;
  }

  get isSetScaleToolActive(): boolean {
    return this.value.activeToolName === GtoToolName.SetScale;
  }

  get isCropToolActive(): boolean {
    return this.value.activeToolName === GtoToolName.Crop;
  }

  get isDrawingMeasurementValid(): boolean {
    const { drawingTop, drawingMeasurement } = this;

    if (isNil(drawingTop) || isNil(drawingMeasurement)) {
      return false;
    }

    return isGraphicalTakeOffTopViewValid(drawingTop, drawingMeasurement);
  }

  get canSnapToStart(): boolean {
    const { drawingTop, drawingMeasurement } = this;

    if (isNil(drawingTop) || isNil(drawingMeasurement)) {
      return false;
    }

    return isGraphicalTakeOffTopViewCanSnapToStart(drawingTop, drawingMeasurement);
  }

  get canStartNewArea(): boolean {
    const { isInTopDrawingMode, isDrawingMeasurementValid } = this;

    if (isInTopDrawingMode) {
      return isDrawingMeasurementValid;
    }

    const { currentTopView } = this;

    if (currentTopView) {
      if (!isGraphicalTakeOffLengthType(getGraphicalTakeOffTopType(currentTopView))) {
        return false;
      }

      return this.isInTopDrawingMode;
    }

    return false;
  }

  get isInTopDrawingMode(): boolean {
    return isNotNil(this.value.drawingMeasurement);
  }

  get isMeasurementInProgress(): boolean {
    return this.value.measurementInProgress;
  }

  get isScalingInProgress(): boolean {
    return this.value.tools.scaling.inProgress;
  }

  get isSnapToPoint(): boolean {
    return this.value.isSnapToPoint;
  }

  get canCloneSelectedTop(): boolean {
    if (this.isInTopDrawingMode) {
      return false;
    }

    const { selectedMeasurement, selectedTop } = this;

    if (isNil(selectedTop) || isNil(selectedMeasurement)) {
      return false;
    }

    const type = getGraphicalTakeOffTopType(selectedTop);
    if (isGraphicalTakeOffLengthType(type)) {
      return selectedMeasurement.points.length > 2;
    } else {
      return isGraphicalTakeOffAreaType(type);
    }
  }

  get viewTransform(): GraphicalTakeOffViewTransform {
    return this.value.viewTransform;
  }

  get zoom(): number {
    return this.value.zoom;
  }

  get scale(): number {
    return this.viewTransform.scale;
  }

  get hasDimensionMeasurements(): boolean {
    return this.currentTopViews.some((topView) => {
      const type = getGraphicalTakeOffTopType(topView);
      return (isGraphicalTakeOffAreaType(type) || isGraphicalTakeOffLengthType(type))
        && topView.metadata.measures.length > 0;
    });
  }

  get canUndo(): boolean {
    const { currentView, history } = this.value;
    const viewHistory = history[currentView];
    return viewHistory ? viewHistory.currentIndex > 0 : false;
  }

  get canRedo(): boolean {
    const { currentView, history } = this.value;
    const viewHistory = history[currentView];
    return viewHistory ? viewHistory.currentIndex < viewHistory.records.length - 1 : false;
  }

  get currentScaleLineDirection(): Maybe<GraphicalTakeOffScaleDirection> {
    return this.value.tools.scaling.currentDirection;
  }

  get isScaleCompleted(): boolean {
    const { scaleWidth, scaleHeight } = this.value.tools.scaling;
    return isNotNil(scaleWidth) && isNotNil(scaleHeight);
  }

  get currentScaleWidth(): number {
    return this.value.tools.scaling.scaleWidth || 0;
  }

  get currentScaleHeight(): number {
    return this.value.tools.scaling.scaleHeight || 0;
  }

  get previousScaleWidth(): number {
    return this.value.tools.scaling.previousScaleWidth || 0;
  }

  get previousScaleHeight(): number {
    return this.value.tools.scaling.previousScaleHeight || 0;
  }

  get scaleLinePxLength(): number {
    return this.project(queryGtoScaleLinePxLength);
  }

  get isScaleInfoAccepted(): boolean {
    return this.value.isScaleInfoAccepted;
  }

  get isScaleLineWaitingForLength(): boolean {
    return this.value.tools.scaling.isWaitingForLength;
  }

  get isScaleLineDrawingInProgress(): boolean {
    return !this.isScaleLineWaitingForLength && !this.isScaleCompleted;
  }

  getUndoConflicts(): ID[] {
    return getGtoAffectedViewsHavingAnotherCurrentRecord(this.value);
  }

  getRedoConflicts(): ID[] {
    return getGtoAffectedViewsHaingAnotherNextRecord(this.value);
  }

  getView(viewId: ID): Maybe<GraphicalTakeOffHousePlanView> {
    return graphicalTakeOffHousePlanViewArr.find(this.value.views, viewId);
  }

  getTopViewsForView(viewId: ID): GraphicalTakeOffTopView[] {
    return this.value.topViews[viewId];
  }

  getTopValueInCurrentView(topId: ID): number {
    const { currentSummary, currentView } = this.value;
    const topCurrentSummary = currentSummary[topId];
    return topCurrentSummary ? topCurrentSummary.byViews[currentView] : 0;
  }

  getTopValueInAllViews(topId: ID): number {
    const { currentSummary } = this.value;
    const topCurrentSummary = currentSummary[topId];
    return topCurrentSummary ? topCurrentSummary.allViews : 0;
  }

  isTopChanged(topId: ID): boolean {
    const { currentSummary } = this.value;
    const topCurrentSummary = currentSummary[topId];
    return topCurrentSummary ? topCurrentSummary.isChanged : false;
  }

  initViews(input: GraphicalTakeOffMainConfig, viewDetails: GraphicalTakeOffViewInfo[]): void {
    this.setValue(GRAPHICAL_TAKE_OFF_DETAILS_INIT);
    this.setField('priceBookId', input.priceBookId);
    this.addViews(viewDetails);
  }

  addViews(viewDetails: GraphicalTakeOffViewInfo[]): void {
    this.setField('views', this.value.views.concat(viewDetails.map(({ view }) => view)));
    forEach(viewDetails, ({ tops, topViews, view }) => {
      this.initView(getGraphicalTakeOffHousePlanViewId(view), tops, topViews);
    });
  }

  setView(viewDetail: GraphicalTakeOffViewInfo): void {
    const { view } = viewDetail;
    const viewId = getGraphicalTakeOffHousePlanViewId(view);

    this.setField(
      'views',
      graphicalTakeOffHousePlanViewArr.update(
        this.value.views,
        viewId,
        viewDetail.view));
    this.initView(viewId, viewDetail.tops, viewDetail.topViews);
    this.updateCurrentSummary();
    this.markAsDirtyIfViewHasChanged();
  }

  selectMeasurement(topId: ID, measurementId: ID): void {
    this.setFields({
      selectedTop: topId,
      selectedMeasurement: measurementId,
    });
  }

  unselectMeasurement(): void {
    this.setFields({
      selectedTop: void 0,
      selectedMeasurement: void 0,
    });
  }

  highlightDataBox(topId: ID, measurementId: ID, dataBoxId: ID): void {
    this.setFields({
      highlightedTop: topId,
      highlightedMeasurement: measurementId,
      highlightedDataBox: dataBoxId,
    });
  }

  unhighlightDataBox(): void {
    this.setFields({
      highlightedTop: void 0,
      highlightedMeasurement: void 0,
      highlightedDataBox: void 0,
    });
  }

  setOverlay(overlay: boolean): void {
    this.setField('isOverlay', overlay);
  }

  setDataBoxVisibility(visibility: GraphicalTakeOffDataBoxVisibility): void {
    this.setField('dataBoxVisibility', visibility);
  }

  setActiveTool(toolName: GtoToolName): void {
    this.setField('activeToolName', toolName);

    if (toolName === GtoToolName.SetScale) {
      const { currentView } = this;

      this.setField('tools', setEntityField(this.value.tools, 'scaling', {
        ...GTO_SCALING_TOOL_INIT,
        currentDirection: GraphicalTakeOffScaleDirection.Horizontal,
        previousScaleWidth: currentView.picture.scaleWidth,
        previousScaleHeight: currentView.picture.scaleHeight,
      }));
    }
  }

  resetActiveTool(): void {
    this.setField('activeToolName', void 0);
    this.setField('tools', GTO_TOOLS_INIT);
  }

  resetCurrentView(): void {
    this.setFields({
      selectedTop: void 0,
      selectedMeasurement: void 0,
      drawingTop: void 0,
      drawingMeasurement: void 0,
      highlightedTop: void 0,
      highlightedMeasurement: void 0,
      highlightedDataBox: void 0,
      currentView: <any>void 0,
      selectedView: <any>void 0,
      activeToolName: void 0,
      measurementInProgress: false,
    });
  }

  setCurrentViewId(viewId: ID): void {
    this.setFields({
      currentView: viewId,
      selectedView: viewId,
    });
    this.updateCurrentSummary();
  }

  setSelectedViewId(viewId: ID): void {
    this.setField('selectedView', viewId);
  }

  setCurrentTop(topId: ID): void {
    this.setField('currentTop', topId);
  }

  ensureOnlyVisibleTopSelected(): void {
    if (this.value.isOverlay) {
      // if overlay is not => all tops are visible
      return;
    }

    // when overlay is set => we have to guarantee that only current top can be selected
    if (this.selectedTopId !== this.currentTopId) {
      this.unselectMeasurement();
    }
  }

  /**
   * Set zoom and scale.
   * Zoom is a logical value displayed on UI. It can be set to '0' that means 'Fit to Screen' mode.
   * Scale is an inverted zoom value. When zoom === 0 => it should be recalculated for 'Fit to Screen' mode.
   *
   * @param zoom
   * @param scale
   */
  setZoom(zoom: number, scale: number): void {
    this.setFields({
      viewTransform: setEntityField(this.value.viewTransform, 'scale', scale),
      zoom,
    });

    this.updateView(this.currentViewId, (view) => setEntityField(view, 'zoom', zoom));
  }

  setViewCenter(point: GraphicalTakeOffPoint): void {
    this.updateView(this.currentViewId, (view) => setEntityField(view, 'viewCenter', point));
  }

  setDataBox(topId: ID, measurementId: ID, dataBox: GraphicalTakeOffBox): void {
    this.startChangeDetection();
    this.updateDataBox(topId, measurementId, getGraphicalTakeOffBoxId(dataBox), constant(dataBox));
    this.updateCurrentSummary();
    this.setCurrentViewDirtyIfChanged();
  }

  setDataBoxPosition(topId: ID, measurementId: ID, dataBoxId: ID, point: GraphicalTakeOffPoint): void {
    const { currentTopViews } = this;

    const top = graphicalTakeOffTopViewArr.find(currentTopViews, topId);
    if (isNil(top)) {
      return;
    }

    const isCountType = isGraphicalTakeOffCountType(getGraphicalTakeOffTopType(top));

    const updateBoxPosition = (box: GraphicalTakeOffBox) => setEntityField(box, 'point', point);
    const updateMeasurementBox = (measurementToBeUpdated: GraphicalTakeOffMeasurement) => {
      const { mainDatabox, databoxes } = measurementToBeUpdated;
      if (mainDatabox && getGraphicalTakeOffBoxId(mainDatabox) === dataBoxId) {
        return setEntityField(measurementToBeUpdated, 'mainDatabox', updateBoxPosition(mainDatabox));
      } else if (databoxes) {
        return setEntityField(
          measurementToBeUpdated,
          'databoxes',
          graphicalTakeOffBoxArr.update(databoxes, dataBoxId, updateBoxPosition));
      } else {
        return measurementToBeUpdated;
      }
    };
    const updateMeasurementPosition = (measurementToBeUpdated: GraphicalTakeOffMeasurement) => {
      if (isCountType) {
        return setEntityField(measurementToBeUpdated, 'points', [point]);
      } else {
        return measurementToBeUpdated;
      }
    };

    this.startChangeDetection();
    this.updateMeasurement(topId, measurementId, flow(updateMeasurementBox, updateMeasurementPosition));
    this.updateCurrentSummary();
    this.setCurrentViewDirtyIfChanged();
  }

  beginMeasurementPoint(point: GraphicalTakeOffPoint): void {
    this.setMeasurementInProgress(true);
    this.updateDrawingTop((topView) => this.appendPoint(topView, point));
  }

  setMeasurementPointPosition(point: GraphicalTakeOffPoint): void {
    this.updateDrawingTop((topView) => this.setDraftPointPosition(topView, point));
  }

  commitMeasurementPoint(): void {
    this.startChangeDetection();
    this.setMeasurementInProgress(false);
    this.updateDrawingTop(commitPointForTop);
    this.setCurrentViewDirtyIfChanged();
  }

  rollbackMeasurementPoint(): void {
    this.setMeasurementInProgress(false);
    this.updateDrawingTop(rollbackPointForTop);
  }

  beginScalePoint(point: GraphicalTakeOffPoint): void {
    const { scaling: { point1, point2 } } = this.value.tools;
    if (isNil(point1)) {
      this.setScalingInProgress(true);
      this.setField('tools', setNestedEntityField(this.value.tools, ['scaling', 'point1'], point));
    } else if (isNil(point2)) {
      this.setScalingInProgress(true);
      this.setField('tools', setNestedEntityField(this.value.tools, ['scaling', 'point2'], point));
    }
  }

  setScalePointPosition(point: GraphicalTakeOffPoint): void {
    const { scaling: { point1, point2 } } = this.value.tools;
    if (isNotNil(point2)) {
      this.setField('tools', setNestedEntityField(this.value.tools, ['scaling', 'point2'], point));
    } else if (isNotNil(point1)) {
      this.setField('tools', setNestedEntityField(this.value.tools, ['scaling', 'point1'], point));
    }
  }

  commitScalePoint(): void {
    this.setScalingInProgress(false);
  }

  setCurrentScaleDirection(direction: GraphicalTakeOffScaleDirection): void {
    this.setField('tools', setNestedEntityField(this.value.tools, ['scaling', 'currentDirection'], direction));
  }

  snapToStart(): void {
    const { drawingTop, drawingMeasurement } = this;

    if (drawingTop && drawingMeasurement) {
      const point = first(drawingMeasurement.points);
      if (point) {
        this.beginMeasurementPoint(point);
        this.commitMeasurementPoint();
        this.completeMeasurement();
        this.selectMeasurement(
          getGraphicalTakeOffTopViewId(drawingTop),
          getGraphicalTakeOffMeasurementId(drawingMeasurement));
      }
    }
  }

  getTopView(topId: ID): Maybe<GraphicalTakeOffTopView> {
    const { currentViewId } = this;
    return currentViewId ? this.getTopViewForView(currentViewId, topId) : void 0;
  }

  getTopViewForView(viewId: ID, topId: ID): Maybe<GraphicalTakeOffTopView> {
    return graphicalTakeOffTopViewArr.find(this.getTopViewsForView(viewId), topId);
  }

  getTop(topId: ID): Maybe<GraphicalTakeOffTop> {
    return graphicalTakeOffTopArr.find(this.tops, topId);
  }

  getDataBox(topId: ID, measurementId: ID, dataBoxId: ID): Maybe<GraphicalTakeOffBox> {
    const top = this.getTopView(topId);
    const measurement = top ? graphicalTakeOffMeasurementArr.find(top.metadata.measures, measurementId) : void 0;
    if (measurement) {
      if (measurement.mainDatabox && getGraphicalTakeOffBoxId(measurement.mainDatabox) === dataBoxId) {
        return measurement.mainDatabox;
      } else if (measurement.databoxes) {
        return graphicalTakeOffBoxArr.find(measurement.databoxes, dataBoxId);
      }
    }
  }

  deleteSelectedMeasurement(): void {
    const { selectedTop, selectedMeasurement } = this;

    if (selectedTop && selectedMeasurement) {
      this.startChangeDetection();
      this.unselectMeasurement();
      this.updateTopView(
        getGraphicalTakeOffTopViewId(selectedTop),
        (topView) =>
          setNestedEntityField(
            topView,
            ['metadata', 'measures'],
            graphicalTakeOffMeasurementArr.remove(
              topView.metadata.measures,
              getGraphicalTakeOffMeasurementId(selectedMeasurement))));
      this.setCurrentViewDirtyIfChanged();

      this.updateCurrentSummary();
    }
  }

  deleteActivePoint(): void {
    const { activeTop, activeMeasurement } = this;

    if (isNil(activeTop) || isNil(activeMeasurement)) {
      return;
    }

    const activeTopId = getGraphicalTakeOffTopViewId(activeTop);

    if (activeMeasurement) {
      const selectionId = getGraphicalTakeOffMeasurementId(activeMeasurement);
      this.startChangeDetection();
      if (activeMeasurement.points.length > 1) {
        this.updateMeasurement(
          activeTopId,
          selectionId,
          (measurement) => removeMeasurementPoint(activeTop, measurement));
      } else {
        this.updateTopView(
          activeTopId,
          (top) =>
            setNestedEntityField(
              top,
              ['metadata', 'measures'],
              graphicalTakeOffMeasurementArr.remove(top.metadata.measures, selectionId)));
        this.setCurrentViewDirtyIfChanged();
      }

      this.updateCurrentSummary();
      this.updateDrawingModeForTop(activeTopId);
    }
  }

  addTop(top: GraphicalTakeOffTop): void {
    this.setField('tops', graphicalTakeOffTopArr.insertSorted(this.value.tops, top, ({ code }) => code.toLowerCase()));
  }

  moveTopTo(targetTop: GraphicalTakeOffTop, option: GraphicalTakeOffChangeOption): void {
    const { currentViewId, currentTop, currentTopView } = this;

    const lastMeasurement = currentTopView && getGraphicalTakeOffTopViewLastMeasurement(currentTopView);
    if (option === GraphicalTakeOffChangeOption.CURRENT_SELECTION && isNil(lastMeasurement)) {
      return;
    }

    if (currentViewId && currentTop) {
      const targetViewIds = option === GraphicalTakeOffChangeOption.ALL_VIEWS ?
        this.value.views.map(getGraphicalTakeOffHousePlanViewId)
        : [currentViewId];

      const sourceTopId = getGraphicalTakeOffTopId(currentTop);
      this.startChangeDetection();
      targetViewIds.forEach((viewId) => {
        const sourceTopView = this.getTopViewForView(viewId, sourceTopId);
        if (sourceTopView) {
          const targetMeasurements = option === GraphicalTakeOffChangeOption.CURRENT_SELECTION ?
            [lastMeasurement!]
            : getGraphicalTakeOffTopViewAllMeasurements(sourceTopView);

          const sourceUom = option === GraphicalTakeOffChangeOption.CURRENT_SELECTION ?
            currentTop.gtoUom
            : sourceTopView.takeOffParam.gtoUom;

          this.setTopViewsForView(
            viewId,
            graphicalTakeOffTopViewArr.update(
              this.getTopViewsForView(viewId),
              sourceTopId,
              (topView) => removeGtoTopViewMeasurements(topView, targetMeasurements)));

          this.ensureAndUpdateTopView(
            viewId,
            getGraphicalTakeOffTopId(targetTop),
            (topView) => appendGtoTopViewMeasurements(topView, sourceUom, targetMeasurements));
        }
        this.setViewDirtyIfChanged(viewId);
        this.updateCurrentSummary(viewId);
      });
    }
  }

  setCurrentViewScale(scaleWidth?: number, scaleHeight?: number): void {
    this.updateView(
      this.currentViewId,
      (view) => setEntityField(view, 'picture', setEntityFields(view.picture, { scaleWidth, scaleHeight })));
  }

  commitCurrentViewScale(): void {
    this.updateView(this.currentViewId, (view) => setEntityField(view, 'isScaled', true));
  }

  setViewImage(viewId: ID, image: HTMLImageElement): void {
    this.updateView(
      viewId,
      (view) =>
        setEntityFields(view, { image, imagePipeline: graphicalTakeOffImagePipeline(image), imageLoaded: true }));
  }

  setViewSnapGrid(viewId: ID, corners: GraphicalTakeOffPoint[]): void {
    this.updateView(
      viewId,
      (view) => setEntityFields(view, { corners, snapGridLoaded: true }));
  }

  removeView(viewId: ID): void {
    this.setField('views', graphicalTakeOffHousePlanViewArr.remove(this.value.views, viewId));
    this.setField('topViews', removeEntityField(this.value.topViews, viewId));
    if (this.currentViewId === viewId) {
      this.resetCurrentView();
    }

    this.unmarkViewAsLoaded(viewId);
    this.updateCurrentSummary();
    this.markAsDirtyIfViewHasChanged();
  }

  currentViewImageTransform(transformation: GraphicalTakeOffImageTransform): void {
    this.updateView(
      this.currentViewId,
      (view) =>
        setEntityField(
          view,
          'imagePipeline',
          graphicalTakeOffImageAppendTransform(view.imagePipeline, transformation)));
    this.updateCurrentViewDirty();
  }

  currentViewImageSetTransformation(transformation: GraphicalTakeOffImageTransform): void {
    this.updateView(
      this.currentViewId,
      (view) =>
        setEntityField(
          view,
          'imagePipeline',
          graphicalTakeOffImageSetTransformation(view.imagePipeline, transformation)));
    this.updateCurrentViewDirty();
  }

  currentViewResetTransformations(): void {
    this.updateView(
      this.currentViewId,
      (view) =>
        setEntityField(
          view,
          'imagePipeline',
          graphicalTakeOffImagePipeline(getGraphicalTakeOffViewImage(this.currentView))));
    this.updateCurrentViewDirty();
  }

  completeMeasurement(): void {
    const { drawingTop, drawingMeasurement } = this;

    if (drawingTop && drawingMeasurement) {
      const type = getGraphicalTakeOffTopType(drawingTop);

      if (isGraphicalTakeOffCountType(type)) {
        return;
      }

      const drawingTopId = getGraphicalTakeOffTopViewId(drawingTop);

      this.startChangeDetection();
      this.updateTopView(
        drawingTopId,
        (topView) =>
          setNestedEntityField(
            topView,
            ['metadata', 'measures'],
            graphicalTakeOffMeasurementArr.update(
              topView.metadata.measures,
              getGraphicalTakeOffMeasurementId(drawingMeasurement),
              (measure) => {
                switch (type) {
                  case 'LENGTH':
                    return this.completeLengthMeasurement(topView, measure);
                  case 'AREA':
                    return this.completeAreaMeasurement(topView, measure);
                  default:
                    return measure;
                }
              })));
      this.setCurrentViewDirtyIfChanged();

      this.updateCurrentSummary();
      this.updateDrawingModeForTop(drawingTopId);
    }
  }

  /**
   * Ensures that top having given ID will support appending of new measurement.
   * This top will be either created or switch to append mode.
   *
   * @param topId
   */
  ensureTopAppendMeasurement(topId: ID): void {
    const topView = this.getTopView(topId);
    if (topView) {
      this.switchOnTopAppendMode(topId);
    } else {
      this.ensureAndUpdateTopView(this.currentViewId, topId, identity);
    }
  }

  switchOnTopAppendMode(topId: Maybe<ID> = this.currentTopId): void {
    if (topId) {
      this.updateTopView(
        topId,
        (topView) => setEntityField(topView, 'appendMode', true));
    }
  }

  setSnapToPoint(snapToPoint: boolean): void {
    this.setField('isSnapToPoint', snapToPoint);
  }

  cloneTop(sourceTop: GraphicalTakeOffTopView,
           measurement: GraphicalTakeOffMeasurement,
           targetTop: GraphicalTakeOffTop): void {
    this.startChangeDetection();
    const topId = getGraphicalTakeOffTopId(targetTop);
    this.ensureTopAppendMeasurement(topId);
    this.startDrawing(topId);
    normalizePointsForClone(measurement.points).forEach((point) => {
      this.beginMeasurementPoint(point);
      this.commitMeasurementPoint();
    });
    this.completeMeasurement();
    this.selectMeasurement(getGraphicalTakeOffTopViewId(sourceTop), getGraphicalTakeOffMeasurementId(measurement));
    this.setCurrentViewDirtyIfChanged();
  }

  /**
   * Returns true if user MUST to draw point when its mouse hovers over given measurement
   * @param measurementId
   */
  shouldDrawPointOverMeasurement(measurementId: ID): boolean {

    const { drawingMeasurement } = this;
    if (drawingMeasurement) {
      if (!this.isDrawingMeasurementValid) {
        // allow to choose another measurement only if currently drawing measurement is valid
        return true;
      } else if (getGraphicalTakeOffMeasurementId(drawingMeasurement) === measurementId) {
        // do not allow to choose currently drawing measurement
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  rescale(): void {
    const { scaling } = this.value.tools;
    const { currentView } = this;

    const scaleWidthRatio = (currentView.picture.scaleWidth || 0) / (scaling.previousScaleWidth || 1);
    const scaleHeightRatio = (currentView.picture.scaleHeight || 0) / (scaling.previousScaleHeight || 1);

    const rescaleLengthDataBox = (topView: GraphicalTakeOffTopView,
                                  measurment: GraphicalTakeOffMeasurement,
                                  dataBox: GraphicalTakeOffBox,
                                  pStart: GraphicalTakeOffPoint,
                                  pEnd: GraphicalTakeOffPoint) =>
      setDataboxOriginalValue(
        dataBox,
        scaleLength(
          topView.takeOffParam.gtoUom,
          getDataBoxOriginalValue(dataBox),
          pStart,
          pEnd,
          scaleWidthRatio,
          scaleHeightRatio));

    const rescaleAreaMainDataBox = (topView: GraphicalTakeOffTopView,
                                    measurment: GraphicalTakeOffMeasurement,
                                    dataBox: GraphicalTakeOffBox) =>
      setDataboxOriginalValue(
        dataBox,
        scaleArea(
          topView.takeOffParam.gtoUom,
          getDataBoxOriginalValue(dataBox),
          scaleWidthRatio,
          scaleHeightRatio));

    const rescaleLengthMeasurement = (topView: GraphicalTakeOffTopView, measurement: GraphicalTakeOffMeasurement) =>
      setEntityField(
        measurement,
        'databoxes',
        graphicalTakeOffBoxArr.updateAll(
          measurement.databoxes || [],
          (dataBox, i) =>
            rescaleLengthDataBox(
              topView,
              measurement,
              dataBox,
              measurement.points[i],
              measurement.points[i < measurement.points.length - 1 ? i + 1 : 0])));

    const rescaleAreaMeasurement = (topView: GraphicalTakeOffTopView, measurement: GraphicalTakeOffMeasurement) =>
      setEntityField(
        measurement,
        'mainDatabox',
        rescaleAreaMainDataBox(topView, measurement, measurement.mainDatabox!));

    const rescaleTopView = (topView: GraphicalTakeOffTopView) =>
      setNestedEntityField(
        topView,
        ['metadata', 'measures'],
        graphicalTakeOffMeasurementArr.updateAll(
          topView.metadata.measures,
          (measurement) => {
            switch (getGraphicalTakeOffTopType(topView)) {
              case 'LENGTH':
                return rescaleLengthMeasurement(topView, measurement);
              case 'AREA':
                return rescaleAreaMeasurement(topView, measurement);
              default:
                return measurement;
            }
          }));

    this.startChangeDetection();
    this.setTopViewsForView(
      this.currentViewId,
      graphicalTakeOffTopViewArr.updateAll(
        this.currentTopViews,
        (topView) => consistencyTopViewTransform(rescaleTopView(topView))));
    this.commitCurrentViewScale();
    this.resetActiveTool();
    this.setCurrentViewDirtyIfChanged();
  }

  migrateTopViews(viewId: ID, shift: GraphicalTakeOffPoint): void {
    const topViews = this.getTopViewsForView(viewId);

    const migratePoint = (point: GraphicalTakeOffPoint) => addPoint(point, shift);

    const migrateDatabox = (box: GraphicalTakeOffBox) => setEntityField(box, 'point', migratePoint(box.point));

    const migrateMeasurement = (measurement: GraphicalTakeOffMeasurement) =>
      setEntityFields(measurement, {
        points: measurement.points.map(migratePoint),
        mainDatabox: measurement.mainDatabox ? migrateDatabox(measurement.mainDatabox) : void 0,
        databoxes: map(measurement.databoxes, migrateDatabox),
      });

    const migrateTopView = (topView: GraphicalTakeOffTopView) => {
      if (isGraphicalTakeOffTopViewNonMigrated(topView)) {
        return setEntityFields(topView, {
          metadata: setEntityFields(topView.metadata, {
            version: 1,
            measures: graphicalTakeOffMeasurementArr.updateAll(topView.metadata.measures, migrateMeasurement),
          }),
        });
      } else {
        return topView;
      }
    };

    const hasNonMigratedTops = topViews.some(isGraphicalTakeOffTopViewNonMigrated);

    if (!hasNonMigratedTops) {
      return;
    }

    // exclude duplicated tops
    const uniqueTops = flatMap(
      values(groupBy(topViews, getGraphicalTakeOffTopViewId)),
      (topViewGroup) => topViewGroup.length > 1 ?
        setNestedEntityField(
          topViewGroup[0],
          ['metadata', 'measures'],
          flatMap(topViewGroup, ({ metadata: { measures } }) => measures))
        : topViewGroup[0]);

    // migrate measurement/data boxes points
    const topsWithMigratedPoints = graphicalTakeOffTopViewArr.updateAll(
      uniqueTops,
      flow(migrateTopView, consistencyTopViewTransform));

    this.setTopViewsForView(viewId, topsWithMigratedPoints);
  }

  transaction<T>(fn: () => T): T {
    const { value: oldValue, hasOpenedTransaction } = this;
    if (hasOpenedTransaction) {
      return fn();
    } else {
      this.commitPendingTransaction();
      this.hasOpenedTransaction = true;
      const returnedValue = fn();
      this.hasOpenedTransaction = false;
      if (this.value !== oldValue) {
        this.pushHistoryRecord(findGtoChangedViewIds(oldValue, this.value));
      }
      return returnedValue;
    }
  }

  pendingTransaction<T>(fn: () => T): T {
    const oldValue = this.value;
    const returnedValue = fn();
    if (this.value !== oldValue) {
      this.hasPendingTransaction = true;
      this.viewIdsAffectedByPendingTransaction = uniq(this.viewIdsAffectedByPendingTransaction
        .concat(findGtoChangedViewIds(oldValue, this.value)));
    }
    return returnedValue;
  }

  commitPendingTransaction(): void {
    if (this.hasPendingTransaction) {
      this.pushHistoryRecord(this.viewIdsAffectedByPendingTransaction);
      this.hasPendingTransaction = false;
      this.viewIdsAffectedByPendingTransaction = [];
    }
  }

  undo(): void {
    const records = getGtoPreviousViewHistoryRecords(this.value);
    if (records) {
      this.setValue(gtoRestoreDetails(this.value, records, -1));
      keys(records).forEach((viewId) => this.updateCurrentSummary(viewId));
      this.updateCurrentViewDirty();
    }
  }

  redo(): void {
    const records = getGtoNextViewHistoryRecords(this.value);
    if (records) {
      this.setValue(gtoRestoreDetails(this.value, records, 1));
      keys(records).forEach((viewId) => this.updateCurrentSummary(viewId));
      this.updateCurrentViewDirty();
    }
  }

  setScaleLineDirection(direction: GraphicalTakeOffScaleDirection): void {
    this.setField('tools', setNestedEntityField(this.value.tools, ['scaling', 'currentDirection'], direction));
  }

  setScaleLineLength(length: number): void {
    const isHorizontal = this.currentScaleLineDirection === GraphicalTakeOffScaleDirection.Horizontal;

    const scaleBoundPropertyName = isHorizontal ? 'scaleWidth' : 'scaleHeight';
    this.setField('tools', setEntityField(this.value.tools, 'scaling', setEntityFields(this.value.tools.scaling, {
      [scaleBoundPropertyName]: length,
      isWaitingForLength: false,
    })));

    if (isHorizontal) {
      this.setCurrentViewScale(length, this.verticalScale);
    } else {
      this.setCurrentViewScale(this.horizontalScale, length);
    }
  }

  setScaleLineWaitingForLength(waiting: boolean): void {
    this.setField('tools', setNestedEntityField(this.value.tools, ['scaling', 'isWaitingForLength'], waiting));
  }

  resetScaleLine(): void {
    this.setField('tools', setEntityField(this.value.tools, 'scaling', setEntityFields(this.value.tools.scaling, {
      point1: void 0,
      point2: void 0,
    })));
  }

  resetScale(): void {
    const { tools } = this.value;
    const { scaling } = tools;

    this.setField('tools', setEntityField(tools, 'scaling', setEntityFields(scaling, {
      scaleWidth: scaling.previousScaleWidth,
      scaleHeight: scaling.previousScaleHeight,
    })));
  }

  setScaleInfoAccepted(): void {
    this.setField('isScaleInfoAccepted', true);
  }

  setUserPreference(): void {
    const userGTOPreference = sessionStorage.getItem("UserGTOPreferences");
    const currentPreferences: UserGTOPreferences = userGTOPreference ? JSON.parse(userGTOPreference) : {};
    if (currentPreferences?.overLay !== undefined) {
      this.setOverlay(currentPreferences.overLay);
    }
    if (currentPreferences?.snapToPoint !== undefined) {
        this.setSnapToPoint(currentPreferences.snapToPoint);
    }
    if (currentPreferences?.hideShowDataBoxes !== undefined) {
        this.setDataBoxVisibility(currentPreferences.hideShowDataBoxes);
    }
  }

  private ensureFirstHistoryRecord(viewId: ID): void {
    const record = getGtoViewHistoryRecord(this.value, viewId);
    if (isNil(record)) {
      this.pushHistoryRecord([viewId]);
    }
  }

  private pushHistoryRecord(affectedViewIds: ID[]): void {
    this.setValue(pushGtoViewHistoryRecords(this.value, affectedViewIds));
  }

  private ensureAndUpdateTopView(viewId: ID,
                                 topId: ID,
                                 mapTopView: IdentityFn<GraphicalTakeOffTopView>): void {
    const topViews = this.getTopViewsForView(viewId);
    const existingTopView = graphicalTakeOffTopViewArr.find(topViews, topId);
    const topView = existingTopView ? existingTopView : this.createEmptyTopView(viewId, this.getTop(topId)!);
    const updatedTopView = mapTopView(topView);

    if (existingTopView) {
      this.setTopViewsForView(
        viewId,
        graphicalTakeOffTopViewArr.update(topViews, topId, consistencyTopViewTransform(updatedTopView)));
    } else {
      this.setTopViewsForView(
        viewId,
        graphicalTakeOffTopViewArr.append(topViews, consistencyTopViewTransform(updatedTopView)));
    }
  }

  private updateCurrentSummary(viewId: ID = this.value.currentView): void {
    const { currentSummary } = this.value;
    const topViews = this.getTopViewsForView(viewId) || [];

    if (!viewId) {
      this.setField('currentSummary', {});
      return;
    }

    const existingCurrentSummaryKeys = Object.keys(currentSummary);
    const currentTopKeys = topViews.map(getGraphicalTakeOffTopViewId);
    const keysToBeRemoved = difference(existingCurrentSummaryKeys, currentTopKeys);

    const cleanedCurrentSummary = setEntityFields(currentSummary, fromPairs(keysToBeRemoved.map((topId) => {
      const topCurrentSummary = currentSummary[topId];
      return [
        topId,
        setEntityFields(topCurrentSummary, {
          allViews: this.getTopValueByAllViews(topId, viewId),
          byViews: removeEntityField(topCurrentSummary.byViews, viewId),
          selection: void 0,
          isChanged: false,
        }),
      ];
    })));

    this.setField(
      'currentSummary',
      setEntityFields(cleanedCurrentSummary, fromPairs(topViews.map((topView) => {
        const topViewId = getGraphicalTakeOffTopViewId(topView);
        const topCurrentSummary = currentSummary[topViewId]
          || { allViews: 0, byViews: {}, selection: 0, isChanged: false };
        const topCurrentViewValue = graphicalTakeOffTopViewValue(topView);
        return [
          topViewId,
          setEntityFields(topCurrentSummary, {
            allViews: this.getTopValueByAllViews(topViewId, viewId) + topCurrentViewValue,
            byViews: setEntityField(topCurrentSummary.byViews, viewId, topCurrentViewValue),
            selection: graphicalTakeOffTopViewCurrentSelectionValue(topView),
            isChanged: topView.metadata.isChanged,
          }),
        ];
      }))));
  }

  private setCurrentTopViews(topViews: GraphicalTakeOffTopView[]): void {
    this.setTopViewsForView(this.value.currentView, topViews);
  }

  private setTopViewsForView(viewId: ID, topViews: GraphicalTakeOffTopView[]): void {
    this.setField('topViews', setEntityField(this.value.topViews, viewId, topViews));
  }

  private markViewAsLoaded(viewId: ID, tops: GraphicalTakeOffTop[]): void {
    const { persistedSummary, loadedViewIndex, loadedViewCount } = this.value;

    if (!loadedViewIndex[viewId]) {
      this.setField('loadedViewIndex', setEntityField(this.value.loadedViewIndex, viewId, true));
      this.setField('loadedViewCount', loadedViewCount + 1);
    }

    this.setField(
      'history',
      setEntityField(this.value.history, viewId, GTO_VIEW_HISTORY_EMPTY));
    this.ensureFirstHistoryRecord(viewId);

    this.setField(
      'persistedSummary',
      setEntityFields(
        persistedSummary,
        fromPairs(tops.map((top) => {
          const topId = getGraphicalTakeOffTopId(top);
          const persistedTop = persistedSummary[topId] || { allViews: top.isFromGto ? top.value : 0, byViews: {} };
          return [
            topId,
            setEntityFields(persistedTop, {
              byViews: setEntityField(persistedTop.byViews, viewId, top.viewValue || 0),
            }),
          ];
        }))));
  }

  private unmarkViewAsLoaded(viewId: ID): void {
    const { persistedSummary, loadedViewIndex, loadedViewCount } = this.value;

    if (loadedViewIndex[viewId]) {
      this.setField('loadedViewIndex', removeEntityField(this.value.loadedViewIndex, viewId));
      this.setField('loadedViewCount', loadedViewCount - 1);
    }

    this.setField('history', removeEntityField(this.value.history, viewId));

    this.setField('persistedSummary', fromPairs(Object.keys(persistedSummary).map((topId) => {
      const persistedTopSummary = persistedSummary[topId];
      return [
        topId,
        setEntityFields(
          persistedTopSummary,
          {
            byViews: removeEntityField(persistedTopSummary.byViews, viewId),
            allViews: persistedTopSummary.allViews - (persistedTopSummary.byViews[viewId] || 0),
          }),
      ];
    })));
  }

  private updateTopView(topId: ID, updater: IdentityFn<GraphicalTakeOffTopView>): void {
    this.setCurrentTopViews(
      graphicalTakeOffTopViewArr.update(
        this.currentTopViews,
        topId,
        flow(updater, consistencyTopViewTransform)));
  }

  private updateMeasurement(topId: ID, measurementId: ID, updater: IdentityFn<GraphicalTakeOffMeasurement>): void {
    this.updateTopView(
      topId,
      (top) => setNestedEntityField(
        top,
        ['metadata', 'measures'],
        graphicalTakeOffMeasurementArr.update(
          top.metadata.measures,
          measurementId,
          updater)));
  }

  private updateDataBox(topId: ID,
                        measurementId: ID,
                        dataBoxId: ID,
                        updater: IdentityFn<GraphicalTakeOffBox>): void {
    this.updateMeasurement(topId, measurementId, (measurement: GraphicalTakeOffMeasurement) => {
      const { mainDatabox, databoxes } = measurement;
      if (mainDatabox && getGraphicalTakeOffBoxId(mainDatabox) === dataBoxId) {
        return setEntityField(measurement, 'mainDatabox', updater(mainDatabox));
      } else if (databoxes) {
        return setEntityField(
          measurement,
          'databoxes',
          graphicalTakeOffBoxArr.update(databoxes, dataBoxId, updater));
      } else {
        return measurement;
      }
    });
  }

  private appendPoint(topView: GraphicalTakeOffTopView, point: GraphicalTakeOffPoint): GraphicalTakeOffTopView {
    const type = getGraphicalTakeOffTopType(topView);

    if (isGraphicalTakeOffCountType(type)) {
      return setNestedEntityField(
        topView,
        ['metadata', 'measures'],
        graphicalTakeOffMeasurementArr.append(
          topView.metadata.measures,
          createCountMeasurement(point, this.translations.get('lbl_count'))));
    } else if (isGraphicalTakeOffLengthType(type)) {
      const { measures } = topView.metadata;
      const lastMeasurement = last(measures)!;  

      return setEntityFields(topView, {
        metadata: setEntityFields(topView.metadata, {
          measures: shouldAppendMeasurement(topView) ?
            graphicalTakeOffMeasurementArr.append(measures, createLengthMeasurement(point))
            : graphicalTakeOffMeasurementArr.update(
              measures,
              getGraphicalTakeOffMeasurementId(lastMeasurement),
              addPointIntoLengthMeasurement(
                this.value,
                topView,
                lastMeasurement,
                point,
                this.translations.get('lbl_length'))),
        }),
        appendMode: false,
      });
    } else if (isGraphicalTakeOffAreaType(type)) {
      const { measures } = topView.metadata;
      const lastMeasurement = last(measures)!;

      return setEntityFields(topView, {
        metadata: setEntityFields(topView.metadata, {
          measures: shouldAppendMeasurement(topView) ?
            graphicalTakeOffMeasurementArr.append(measures, createAreaMeasurement(point))
            : graphicalTakeOffMeasurementArr.update(
              measures,
              getGraphicalTakeOffMeasurementId(lastMeasurement),
              addPointIntoAreaMeasurement(
                this.value,
                topView,
                lastMeasurement,
                point)),
        }),
        appendMode: false,
      });
    } else {
      return topView;
    }
  }

  private setDraftPointPosition(topView: GraphicalTakeOffTopView,
                                point: GraphicalTakeOffPoint): GraphicalTakeOffTopView {
    const type = getGraphicalTakeOffTopType(topView);

    const { measures } = topView.metadata;
    const lastMeasurement = last(measures)!;

    if (isGraphicalTakeOffCountType(type)) {
      return setNestedEntityField(
        topView,
        ['metadata', 'measures'],
        graphicalTakeOffMeasurementArr.update(
          topView.metadata.measures,
          getGraphicalTakeOffMeasurementId(lastMeasurement),
          setPointForCountMeasurement(lastMeasurement, point)));
    } else if (isGraphicalTakeOffLengthType(type)) {
      return setEntityFields(topView, {
        metadata: setEntityFields(topView.metadata, {
          measures: graphicalTakeOffMeasurementArr.update(
            measures,
            getGraphicalTakeOffMeasurementId(lastMeasurement),
            setPointForLengthMeasurement(this.value, topView, lastMeasurement, point)),
        }),
      });
    } else if (isGraphicalTakeOffAreaType(type)) {
      return setEntityFields(topView, {
        metadata: setEntityFields(topView.metadata, {
          measures: graphicalTakeOffMeasurementArr.update(
            measures,
            getGraphicalTakeOffMeasurementId(lastMeasurement),
            setPointForAreaMeasurement(this.value, topView, lastMeasurement, point)),
        }),
      });
    } else {
      return topView;
    }
  }

  private createEmptyTopView(viewId: ID, top: GraphicalTakeOffTop): GraphicalTakeOffTopView {
    const view = this.getView(viewId)!;

    return {
      id: void 0,
      hpViewId: view.hpViewId,
      jobViewId: view.jobViewId,
      takeOffParam: {
        takeOffParamId: top.takeOffParamId,
        gtoColor: top.gtoColor,
        gtoUom: top.gtoUom,
        gtoStyle: top.gtoStyle,
        code: top.code,
      },
      metadata: {
        isChanged: false,
        measures: [],
        type: getGraphicalTakeOffTopType(top),
        version: 2,
      },
      appendMode: false,
    };
  }

  private getTopValueByAllViews(topViewId: ID, excludeViewId: ID): number {
    const { persistedSummary, currentSummary } = this.value;

    const currentTopSummary = currentSummary[topViewId];
    const sumByLoadedViews = currentTopSummary ?
      reduce(
        currentTopSummary.byViews,
        (accSum, topSummary, key) => {
          if (key === excludeViewId) {
            return accSum;
          } else {
            return accSum + topSummary;
          }
        },
        0)
      : 0;

    const persistedTopSummary = persistedSummary[topViewId];
    const sumByNotLoadedViews = persistedTopSummary ?
      sum(values(omit(
        currentTopSummary ?
          pick(
            persistedTopSummary.byViews,
            difference(keys(persistedTopSummary.byViews), keys(currentTopSummary.byViews)))
          : persistedTopSummary.byViews,
        excludeViewId)))
      : 0;

    return sumByLoadedViews + sumByNotLoadedViews;
  }

  private initView(viewId: ID, tops: GraphicalTakeOffTop[], topViews: GraphicalTakeOffTopView[]): void {
    if (this.value.tops.length === 0) {
      this.setField('tops', tops);
      const firstTop = first(tops);
      if (firstTop) {
        this.setCurrentTop(getGraphicalTakeOffTopId(firstTop));
      }
    }
    this.setTopViewsForView(viewId, topViews);
    this.markViewAsLoaded(viewId, tops);
    this.updateCurrentSummary(viewId);
    this.setUserPreference();
  }

  private startDrawing(drawingTop: ID, drawingMeasurement?: ID): void {
    this.setFields({
      drawingTop,
      drawingMeasurement,
    });
  }

  private stopDrawing(): void {
    this.setFields({
      drawingTop: void 0,
      drawingMeasurement: void 0,
    });
  }

  private updateDrawingModeForTop(topId: ID): void {
    const activeTop = this.getTopView(topId);
    if (activeTop && !isGraphicalTakeOffCountType(getGraphicalTakeOffTopType(activeTop))) {
      // try to find measurement with started drawing mode
      const drawingMeasurement = find(activeTop.metadata.measures, { drawingMode: true });
      if (drawingMeasurement) {
        this.startDrawing(topId, getGraphicalTakeOffMeasurementId(drawingMeasurement));
      } else {
        this.stopDrawing();
      }
    }
  }

  private updateDrawingTop(transform: IdentityFn<GraphicalTakeOffTopView>): void {
    const { currentViewId } = this;

    const { drawingTop, currentTop } = this.value;
    const topId = drawingTop || currentTop;
    if (isNil(topId)) {
      return;
    }

    this.ensureAndUpdateTopView(currentViewId, topId, transform);
    this.updateCurrentSummary();

    this.updateDrawingModeForTop(topId);
  }

  private setMeasurementInProgress(measurementInProgress: boolean): void {
    this.setField('measurementInProgress', measurementInProgress);
  }

  private completeLengthMeasurement(topView: GraphicalTakeOffTopView,
                                    measurement: GraphicalTakeOffMeasurement): GraphicalTakeOffMeasurement {
    return setEntityFields(measurement, {
      drawingMode: false,
      draftMode: false,
      mainDatabox: createDatabox(middlePoint(...measurement.points), 1, this.translations.get('lbl_perimeter')),
    });
  }

  private completeAreaMeasurement(topView: GraphicalTakeOffTopView,
                                  measurement: GraphicalTakeOffMeasurement): GraphicalTakeOffMeasurement {
    const { measurementDefault, picture } = this.currentView!;

    const scaledPoint = measurement.points
      .map((point) => scalePoint(point, picture.scaleWidth || 0, picture.scaleHeight || 0));
    const area = truncateValueByUom(
      convertValueUom(
        measurementDefault,
        topView.takeOffParam.gtoUom,
        calculateArea(scaledPoint)),
      topView.takeOffParam.gtoUom);
    return setEntityFields(measurement, {
      drawingMode: false,
      draftMode: false,
      mainDatabox: createDatabox(middlePoint(...measurement.points), area, this.translations.get('lbl_area')),
    });
  }

  private updateView(viewId: ID, mapView: IdentityFn<GraphicalTakeOffHousePlanView>): void {
    this.setField(
      'views',
      graphicalTakeOffHousePlanViewArr.update(
        this.value.views,
        viewId,
        mapView));
  }

  private updateCurrentViewDirty(): void {
    const { currentView } = this;
    const viewId = getGraphicalTakeOffHousePlanViewId(currentView);
    if (currentView.isScaled) {
      this.updateView(viewId, setDirty);
    } else {
      this.updateView(
        viewId,
        (view) => setDirtyField(view, getGraphicalTakeOffViewImagePipeline(view).transformations.length > 0));
    }
    this.markAsDirtyIfViewHasChanged();
  }

  private setViewDirtyIfChanged(viewId: ID): void {
    if (this.changed) {
      this.updateView(viewId, setDirty);
      this.markAsDirtyIfViewHasChanged();
      this.endChangeDetection();
    }
  }

  private setCurrentViewDirtyIfChanged(): void {
    this.setViewDirtyIfChanged(this.currentViewId);
  }

  private markAsDirtyIfViewHasChanged(): void {
    this.setDirty(this.views.some(isDirty));
  }

  private setScalingInProgress(inProgress: boolean): void {
    this.setField('tools', setNestedEntityField(this.value.tools, ['scaling', 'inProgress'], inProgress));
  }
}
